/**
 * Format catalog / issued / milestone rewards for UI.
 * Mirrors backend/reward_display.py type mapping.
 */

const TYPE_MAP = {
  money: "cash_value",
  item: "free_item",
  percent_discount: "percent_discount",
  cash_value: "cash_value",
  free_item: "free_item",
  item_choice: "item_choice",
  bonus_points: "bonus_points",
};

function normalizeType(raw) {
  if (!raw) return "cash_value";
  return TYPE_MAP[String(raw).trim()] || String(raw).trim();
}

function fmtGbp(value) {
  const v = Number(value) || 0;
  return Number.isInteger(v) ? `£${v}` : `£${v.toFixed(2)}`;
}

function optionsList(options) {
  if (!Array.isArray(options)) return [];
  return options
    .map((o) => {
      if (typeof o === "string") return o.trim();
      if (o && typeof o === "object") return (o.name || o.label || o.title || "").trim();
      return "";
    })
    .filter(Boolean);
}

/**
 * @returns {{ title: string, summary: string, short: string, reward_type: string, item_options: string[] }}
 */
export function formatRewardSummary(raw = {}) {
  if (raw.reward_preview) return formatRewardSummary({ ...raw, ...raw.reward_preview, reward_preview: undefined });
  if (raw.summary && raw.title) {
    return {
      title: raw.title,
      summary: raw.summary,
      short: raw.short || raw.summary,
      reward_type: normalizeType(raw.reward_type),
      item_options: optionsList(raw.item_options),
    };
  }

  const cat = raw.catalog || {};
  const src = {
    ...raw,
    name: cat.name || raw.name || raw.label,
    label: raw.label || cat.name,
    reward_type: cat.reward_type || raw.reward_type || raw.type,
    description: cat.description || raw.description || raw.reward_description,
    reward_description: raw.reward_description || cat.description,
    value_gbp: cat.value_gbp ?? raw.value_gbp,
    discount_percent: cat.discount_percent ?? raw.discount_percent,
    points_value: cat.points_value ?? raw.points_value,
    item_options: cat.item_options || raw.item_options || [],
  };

  const rtype = normalizeType(src.reward_type);
  const title = (src.name || src.label || src.description || src.reward_description || "Reward").trim() || "Reward";
  const desc = (src.description || src.reward_description || "").trim();
  const options = optionsList(src.item_options);

  let summary;
  let short;

  if (rtype === "percent_discount") {
    const pct = Number(src.discount_percent) || 0;
    const detail = `${Number.isInteger(pct) ? pct : pct}% off`;
    summary = title.toLowerCase() === "reward" ? detail : `${title} — ${detail}`;
    short = detail;
  } else if (rtype === "bonus_points") {
    const pts = parseInt(src.points_value, 10) || 0;
    const detail = `+${pts} points`;
    summary = title.toLowerCase() === "reward" ? detail : `${title} — ${detail}`;
    short = detail;
  } else if (rtype === "item_choice") {
    const detail = options.length ? `Choose: ${options.slice(0, 4).join(", ")}${options.length > 4 ? "…" : ""}` : (desc || "Choose an item");
    summary = title.toLowerCase() === "reward" ? detail : `${title} — ${detail}`;
    short = title.toLowerCase() === "reward" ? detail : title;
  } else if (rtype === "free_item") {
    if (title.toLowerCase() === "reward") {
      summary = desc || "Free item";
      short = summary;
    } else {
      summary = !desc || desc === title ? title : `${title} — ${desc}`;
      short = title;
    }
  } else {
    const val = Number(src.value_gbp) || 0;
    if (val > 0) {
      const cash = fmtGbp(val);
      if (title.toLowerCase() === "reward" || title.toLowerCase() === cash.toLowerCase()) {
        summary = cash;
        short = cash;
      } else {
        summary = `${title} — ${cash}`;
        short = cash;
      }
    } else {
      summary = title.toLowerCase() === "reward" ? (desc || "Reward") : title;
      short = summary;
    }
  }

  return { title, summary, short, reward_type: rtype, item_options: options };
}

export function rewardDisplayLine(raw) {
  const f = formatRewardSummary(raw || {});
  return f.summary || f.title || "Reward";
}
